import React, { useEffect, useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TextField,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Paper,
  Typography,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Select,
  MenuItem,
  Pagination,
  TableContainer,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  getAllLocationListAction,
  getDeleteLocationAction,
  searchLocationListAction
} from '../../redux/actions/locationActions';
import { useDispatch, useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from "@mui/material/styles";
import Loader from '../loader/loader';
import { useNavigate } from 'react-router-dom';
import { stateList } from '../../common/StaticFieldList';
import NoDataFound from "../../common/NoDataFound";

function LocationList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const locationDetails = useSelector((state) => state?.location?.get_all_location_list?.content);
  const pagination_data = useSelector((state) => state?.location?.get_all_location_list);
  const locationData = Array.isArray(locationDetails) ? locationDetails : [];
  const isLoading = useSelector((state) => state.globalSearchReducer.loading);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (isSearchActive && searchKeyword.trim()) {
      dispatch(searchLocationListAction(searchKeyword.trim(), page, rowsPerPage));
    }
  }, [dispatch, page, rowsPerPage, isSearchActive,searchTrigger]);

  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!isSearchActive && !isFetchingRef.current) {
      isFetchingRef.current = true;

      dispatch(getAllLocationListAction(page, rowsPerPage))
        .finally(() => {
          isFetchingRef.current = false;
        });
    }
  }, [dispatch, page, rowsPerPage, isSearchActive]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditClick = (user) => {
    navigate('/addlocation', { state: { locationData: user } });
  };

  const handleSearch = () => {
    if (!searchKeyword.trim()) {
      setSearchError('Please enter a location name to search');
      return;
    }
    
    setSearchError('');
    setPage(0);
    setSearchTrigger(prev => prev + 1);
    setIsSearchActive(true);
  };

  const handleResetSearch = () => {
    setSearchKeyword('');
    setSearchError('');
    setIsSearchActive(false);
    setPage(0);
    setSearchTrigger(0);
  };

  const handleDeleteClick = (id) => {
    setUserToDelete(id);
    setIsDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(getDeleteLocationAction(userToDelete, (success, error) => {
      if (success) {
        dispatch(getAllLocationListAction(page, rowsPerPage));
        setSnackbar({
          open: true,
          message: 'Location deleted successfully',
          severity: 'success',
        });
      } else {
        const errorMessage = error?.errors?.[0]?.errorMessage;
        setSnackbar({
          open: true,
          message: errorMessage || 'Unable to delete location. Customers are currently assigned to it.\nPlease reassign or remove these customers before deleting.',
          severity: 'error',
        });
      }
    }));

    setIsDialogOpen(false);
    setUserToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDialogOpen(false);
    setUserToDelete(null);
  };

  const handleAddLocationClick = () => {
    navigate('/addlocation');
  };

  const getTruncatedText = (text, maxLength = 15) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatPhone = (val) => {
    let digits = String(val ?? '').replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return val || 'N/A';
  };

  const paginatedRows = locationData;


  const renderNoDataFound = () => {
    if (!isSearchActive) {

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <NoDataFound
            discription="Please use the search filter above to fetch and display updated Location data."
            message="After click Search - results will be shown as per selected filter"
          />
        </Box>
      );
    } else if (locationData.length === 0) {

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <NoDataFound
            title="No Data Found"
            discription="Please change search filter where data is available as per defined criteria"
            message="After click Search - results will be shown as per selected filter"
          />
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{
      padding: isMobile ? 2 : 3,
      overflowX: 'auto',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      <Paper sx={{
        padding: isMobile ? 2 : 3,
        boxShadow: isMobile ? 1 : 3
      }}>

        <Typography
          variant={isMobile ? "h6" : "h2"}
          sx={{
            marginBottom: isMobile ? 2 : 3,
          }}
        >
          {isSearchActive
            ? `All Locations (${pagination_data?.totalElements || 0})`
            : 'All Locations'
          }
        </Typography>

        {/* Search filters in a bordered container */}
        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <Box sx={{
              flex: 1,
              minWidth: 0
            }}>
              <Typography
                id="search-location-label"
              >
                Search Location By Name
              </Typography>

              <Box sx={{
                display: "flex",
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 2,
                alignItems: isMobile ? 'stretch' : 'flex-start'
              }}>
                <Box sx={{ 
                  minWidth: isMobile ? 'auto' : 250,
                  width: isMobile ? '100%' : 'auto',
                }}>
                  <TextField
                    variant="outlined"
                    value={searchKeyword}
                    onChange={(e) => {
                      setSearchKeyword(e.target.value);
                      if (searchError && e.target.value.trim()) {
                        setSearchError('');
                      }
                    }}
                    size={isMobile ? "small" : "medium"}
                    error={!!searchError}
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-root': {
                        height: isMobile ? '40px' : '56px'
                      }
                    }}
                    placeholder="Enter location name..."
                  />
                  {searchError && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#d32f2f',
                        fontSize: isMobile ? '11px' : '12px',
                        marginTop: '4px',
                        display: 'block',
                        marginLeft: '14px'
                      }}
                    >
                      {searchError}
                    </Typography>
                  )}
                </Box>

                <Box sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: isMobile ? 'row' : 'row'
                }}>
                  <Button
                    variant='outlined'
                    onClick={handleSearch}
                    disabled={searchLoading}
                  >
                    {searchLoading ? "Searching..." : "Search"}
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={handleResetSearch}
                    disabled={searchLoading}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={handleAddLocationClick}
              id="addnewlocationbutton"
            >
              Add New Location
            </Button>
          </Box>
        </Box>

        {/* Conditional rendering based on search state and data availability */}
        { locationData.length === 0 ? (
          renderNoDataFound()
        ) : (

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: isMobile ? '60vh' : 'none',
              overflow: 'auto',
              boxShadow: isMobile ? 1 : 2
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: isMobile ? '800px' : '650px',
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '11px' : '14px',
                  padding: isMobile ? '6px 8px' : '12px 16px',
                  whiteSpace: isMobile ? 'nowrap' : 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '120px' : '200px'
                }
              }}
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Description
                  </TableCell>
                  <TableCell>
                    Agency Name
                  </TableCell>
                  <TableCell>
                    Address
                  </TableCell>
                  <TableCell>
                    City
                  </TableCell>
                  <TableCell>
                    State
                  </TableCell>
                  <TableCell>
                    Phone
                  </TableCell>
                  <TableCell>
                    Created By
                  </TableCell>
                  <TableCell>
                    Active
                  </TableCell>
                  <TableCell>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading || searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
                        <Loader />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((location, index) => (
                    <TableRow className="styled-table-row" key={location.id || index}>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip
                            title={location.locationDescription || location.description || location.location_description || location.name || "N/A"}
                            arrow
                            placement="top"
                          >
                            <span>
                              {getTruncatedText(location.locationDescription || location.description || location.location_description || location.name)}
                            </span>
                          </Tooltip>
                        ) : (
                          location.locationDescription || location.description || location.location_description || location.name || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={location.agencyName || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(location.agencyName)}</span>
                          </Tooltip>
                        ) : (
                          location.agencyName || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip
                            title={`${location.address1 || ""} ${location.address2 || ""}`.trim() || "N/A"}
                            arrow
                            placement="top"
                          >
                            <span>
                              {getTruncatedText(`${location.address1 || ""} ${location.address2 || ""}`.trim(), 20)}
                            </span>
                          </Tooltip>
                        ) : (
                          `${location.address1 || ""} ${location.address2 || ""}`.trim() || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={location.city || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(location.city)}</span>
                          </Tooltip>
                        ) : (
                          location.city || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip
                            title={
                              stateList.find(
                                (s) => s.value === (location.state || "").trim().toUpperCase()
                              )?.label || location.state || "N/A"
                            }
                            arrow
                            placement="top"
                          >
                            <span>
                              {getTruncatedText(
                                stateList.find(
                                  (s) => s.value === (location.state || "").trim().toUpperCase()
                                )?.label || location.state || "N/A"
                              )}
                            </span>
                          </Tooltip>
                        ) : (
                          stateList.find(
                            (s) => s.value === (location.state || "").trim().toUpperCase()
                          )?.label || location.state || "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={formatPhone(location.phone)} arrow placement="top">
                            <span>{formatPhone(location.phone)}</span>
                          </Tooltip>
                        ) : (
                          formatPhone(location.phone)
                        )}
                      </TableCell>
                      <TableCell>
                        {isMobile ? (
                          <Tooltip title={location?.createdBy?.name || "N/A"} arrow placement="top">
                            <span>{getTruncatedText(location?.createdBy?.name)}</span>
                          </Tooltip>
                        ) : (
                          location?.createdBy?.name || "N/A"
                        )}
                      </TableCell>
                      <TableCell>{location.isActive ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            onClick={() => handleEditClick(location)}
                            aria-label="Edit"
                            sx={{ p: isMobile ? '4px' : '8px' }}
                          >
                            <EditIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            onClick={() => handleDeleteClick(location.id)}
                            aria-label="Delete"
                            sx={{ p: isMobile ? '4px' : '8px' }}
                          >
                            <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination - only show when there's data and search is active */}
        { locationData.length > 0 && (
          <Box sx={{
            width: "100%",
            overflow: "hidden",
            padding: isMobile ? "12px 0" : "16px 0"
          }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="flex-end"
              alignItems="center"
              flexWrap="wrap"
              spacing={2}
              sx={{
                gap: isMobile ? 1 : 2
              }}
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body2" sx={{
                  fontSize: isMobile ? "11px" : "14px",
                  marginRight: "8px"
                }}>
                  Rows per page:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    height: isMobile ? '32px' : '36px',
                    fontSize: isMobile ? '11px' : '14px',
                    minWidth: isMobile ? '60px' : '80px',
                    '& .MuiSelect-select': {
                      padding: isMobile ? '8px 32px 8px 12px' : '10px 32px 10px 12px'
                    }
                  }}
                >
                  {[5, 10, 15, 25, 50, 100].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Pagination
                count={Math.ceil((pagination_data?.totalElements || 0) / rowsPerPage)}
                page={page + 1}
                onChange={(event, value) => handleChangePage(event, value - 1)}
                showFirstButton
                showLastButton
                size={isMobile ? "small" : "medium"}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: isMobile ? '28px' : '32px',
                    height: isMobile ? '28px' : '32px'
                  }
                }}
              />
            </Stack>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDialogOpen}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            handleCancelDelete(event, reason);
          }}
          maxWidth="sm"
          fullWidth={isMobile}
          PaperProps={{
            sx: {
              margin: isMobile ? 1 : 3,
              width: isMobile ? 'calc(100% - 16px)' : 'auto'
            }
          }}
        >
          <DialogTitle
            className="btn-primary"
            sx={{
              height: isMobile ? '56px' : '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '0 12px' : '0 16px',
              fontSize: isMobile ? '16px' : '18px'
            }}
          >
            Confirm Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{
              marginTop: '15px',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              Are you sure you want to delete this location? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{
            padding: isMobile ? '12px 16px' : '16px 24px',
            gap: 1
          }}>
            <Button
              onClick={handleCancelDelete}
              variant='outlined'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant='outlined'
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: isMobile ? 'center' : 'right'
          }}
          sx={{
            top: isMobile ? '60px' : '24px',
            '& .MuiSnackbarContent-root': {
              fontSize: isMobile ? '12px' : '14px'
            }
          }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              whiteSpace: 'pre-line',
              fontSize: isMobile ? '12px' : '14px',
              width: isMobile ? '90vw' : 'auto',
              maxWidth: isMobile ? '350px' : '500px'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}

export default LocationList;